import moment from 'moment'
import qs from 'qs'
import React from 'react'
import { configsActions } from '@/store/configs'
import { getPane, searchActions } from '@/store/search'
import apiWrapper from '@/utils/apiHelpers/apiWrapper'
import { isEmpty as _isEmpty } from 'lodash'
import { getPublications } from '@/utils/publications/helpers'
import { pageActions } from '@/store/page'
import { reduxWrapper } from '@/store'
import { filteredServerSideProps } from '@/utils/apiHelpers/ssrUtils'
import { getInitialStoreSetup } from '@/store/helpers/storeHelpers'
import { generalConstants } from '@tweddle-se-team/common/constants'
import { withLogger } from '@/utils/logger'
import { customerId } from '@/utils/env'
import { customerHelper } from '@tweddle-se-team/common/constants'
import TMEPublications from '@/components/publications'
import SubaruPublications from '@/components/themes/subaru/publications'
import { buildServiceWarrantyInventoryFilters } from '@/utils/publications/serviceWarranty'
import serverSideDataSubaru from '@/components/themes/subaru/publications/serverSideData'
import serverSideDataNordics from '@/components/themes/subaru-nordics/publications/serverSideData'

const { CUSTOMER_IDS } = customerHelper
const { DFOM } = generalConstants

const serverSideDataTME = async (store, ctx, { filters, publications }) => {
  const { query } = ctx
  const { brand, model, modelType, katashiki, productionDate } = filters
  const pane = await getPane(filters)
  const { language } = pane
  // Pane information only used for TME generation
  store.dispatch(searchActions.SET_PANE(pane))
  store.dispatch(pageActions.SET_TITLE('Choose your publication'))

  const dfomList = publications.items.filter(pub => pub.publicationType === DFOM)
  const productionDateObj = new Date(parseInt(productionDate))
  const searchFrom = moment(productionDateObj).subtract(3, 'year').toISOString()
  const searchTo = moment(productionDateObj).add(1, 'year').set('month', 0).set('date', 31).toISOString()

  // check if hotspots are available for the selected model and it is published
  if (dfomList.length) {
    const hotspotFilters = { brand, model, modelType }
    if (searchFrom && searchTo) {
      hotspotFilters.lineOffDate = [searchFrom, searchTo]
    }
    const hotspots = await apiWrapper.get(
      'hotspots',
      { page: 0, limit: 0, isPublished: true, filters: JSON.stringify(hotspotFilters) },
      apiWrapper.getHeaders(ctx)
    )

    if (hotspots.data.length) {
      // Get closest production date hotspot id
      const closestHotspot = hotspots.data.reduce((acc, hotspot) => {
        const hotspotDate = moment.utc(hotspot.lineOffDate)
        const accDate = moment.utc(acc.lineOffDate)
        const hotspotDiff = Math.abs(hotspotDate.diff(productionDateObj))
        const accDiff = Math.abs(accDate.diff(productionDateObj))
        return hotspotDiff < accDiff ? hotspot : acc
      })

      const DFOMQuery = { ...query }

      if (closestHotspot._id && katashiki && productionDate) {
        DFOMQuery.hotspotId = closestHotspot._id
        DFOMQuery.selectedLineOffYear = closestHotspot.year
      }

      const DFMRoute = `/overview?${qs.stringify(DFOMQuery)}`

      return {
        redirect: {
          destination: DFMRoute,
          permanent: false
        }
      }
    }
  }

  const { yearFrom, yearTo } = pane?.generation || {}
  const [availableYears, serviceWarrantyResult] = await Promise.all([
    apiWrapper.get(
      `years/model/${model}/modelType/${modelType}/from/${yearFrom}/to/${yearTo}/lang/${language}`,
      {},
      ctx
    ),
    getPublications(
      buildServiceWarrantyInventoryFilters(filters),
      { page: 1, limit: 100, sort: { lineOffDate: -1 } },
      ctx
    )
  ])

  return {
    props: {
      availableYears,
      productionDate,
      katashiki,
      publications,
      serviceWarrantyPublications: serviceWarrantyResult.items || []
    }
  }
}

const publicationsMapper = {
  [CUSTOMER_IDS.SUBARU]: {
    pageComponent: SubaruPublications,
    serverSideData: serverSideDataSubaru,
    pagination: { page: 1, limit: 50, sort: { year: -1 } }
  },
  [CUSTOMER_IDS.TME]: {
    pageComponent: TMEPublications,
    serverSideData: serverSideDataTME,
    pagination: { page: 1, limit: 50, sort: { lineOffDate: -1 } }
  },
  [CUSTOMER_IDS.NORDICS]: {
    pageComponent: SubaruPublications,
    serverSideData: serverSideDataNordics,
    pagination: { page: 1, limit: 50, sort: { year: -1 } }
  }
}

export const customServerSideProps = async (ctx, store, options = { isPreview: false }) => {
  const { isPreview } = options
  const { serverSideData, pagination } = publicationsMapper[customerId] || {}
  const { query } = ctx
  const { configs } = await getInitialStoreSetup({ store, ctx })
  const { search } = store.getState()
  const filters = _isEmpty(query) ? search.query.filterBy : qs.parse(query, { arrayLimit: 1000 })
  query.isInApp && store.dispatch(configsActions.SET_IS_APP(true))

  let publications = search.results.publications

  if (!_isEmpty(query) || _isEmpty(publications)) {
    try {
      publications = await getPublications(filters, pagination, ctx)
      store.dispatch(searchActions.UPDATE_FILTERS(filters))
      store.dispatch(searchActions.SET_PAGINATION(pagination))
      store.dispatch(searchActions.SET_PUBLICATIONS_RESULT(publications))
    } catch (error) {
      console.error(
        `Error fetching publications: ${error.message}. Filters: ${JSON.stringify(filters)}, pagination: ${JSON.stringify(pagination)}`
      )
      return {
        notFound: true
      }
    }
  }

  // Specific customer server side data
  if (!serverSideData) {
    console.error(`Unsupported customer ID: ${customerId}`)
    return { notFound: true }
  }
  // Note that we need to pass the logger otherwise SWC will try to bundle it for the client side
  // and will fail due to fs not being available on the client side.
  const propsForCustomer = await serverSideData(store, ctx, { configs, filters, publications, isPreview })
  // It can return redirect or not found and other standard Next.js props
  if (!propsForCustomer.props) {
    return propsForCustomer
  }

  return {
    props: filteredServerSideProps(propsForCustomer.props)
  }
}

export const getServerSideProps = reduxWrapper.getServerSideProps(store =>
  withLogger(ctx => customServerSideProps(ctx, store))
)

const Publications = publicationsMapper[customerId]?.pageComponent || (() => <div>Publications page not found</div>)

const PublicationPage = props => <Publications {...props} />

export default PublicationPage
